import React, { useCallback, useEffect, useState } from 'react';
import {
  getTransportBannerKind,
  isMacOsDegradedIpcTransportHost,
  subscribeTransportDegraded,
} from '../lib/ipcTransportDegraded';
import { fetchConnectorLatestPublishedVersion } from '../utils/connectorLatestVersion';

const BROKEN_DISMISS_KEY = 'maestro_ipc_transport_degraded_banner_dismissed';
const RELAY_DISMISS_KEY = 'maestro_ipc_transport_relay_note_dismissed';

function parseVersion(v) {
  if (!v) return null;
  const stripped = v.replace(/^v/i, '');
  const parts = stripped.split('.').map(Number);
  if (parts.some(Number.isNaN)) return null;
  return { major: parts[0] || 0, minor: parts[1] || 0, patch: parts[2] || 0, raw: stripped };
}

function isOutdated(current, latest) {
  if (!current || !latest) return false;
  if (latest.major > current.major) return true;
  if (latest.major === current.major && latest.minor > current.minor) return true;
  if (latest.major === current.major && latest.minor === current.minor && latest.patch > current.patch) return true;
  return false;
}

function brokenBannerMessage(updatePending) {
  const base = 'macOS blocked Maestro\u2019s direct command link on this Mac. Transport commands may not reach your renderer.';
  if (updatePending) {
    return `${base} Restart Maestro after updating to pick up the fix.`;
  }
  return base;
}

/**
 * macOS-only transport notices (#272, #275, #309).
 * Relay-healed hosts get a quiet note; truly broken transport gets a plain warning.
 */
export default function IpcTransportDegradedBanner() {
  const [bannerKind, setBannerKind] = useState(() => getTransportBannerKind());
  const [dismissedBroken, setDismissedBroken] = useState(() => {
    try {
      return sessionStorage.getItem(BROKEN_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [dismissedRelay, setDismissedRelay] = useState(() => {
    try {
      return sessionStorage.getItem(RELAY_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [updatePending, setUpdatePending] = useState(false);

  useEffect(() => subscribeTransportDegraded(() => {
    setBannerKind(getTransportBannerKind());
  }), []);

  useEffect(() => {
    if (bannerKind !== 'broken_loud') return;
    const connectorVersion = window.__MAESTRO_CONNECTOR__?.version || null;
    const current = parseVersion(connectorVersion);
    if (!current) return;

    let cancelled = false;
    (async () => {
      try {
        const latestRaw = await fetchConnectorLatestPublishedVersion();
        const latest = parseVersion(latestRaw);
        if (!cancelled) {
          setUpdatePending(isOutdated(current, latest));
        }
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [bannerKind]);

  const onDismissBroken = useCallback(() => {
    setDismissedBroken(true);
    try {
      sessionStorage.setItem(BROKEN_DISMISS_KEY, '1');
    } catch { /* ignore */ }
  }, []);

  const onDismissRelay = useCallback(() => {
    setDismissedRelay(true);
    try {
      sessionStorage.setItem(RELAY_DISMISS_KEY, '1');
    } catch { /* ignore */ }
  }, []);

  if (!isMacOsDegradedIpcTransportHost()) return null;

  if (bannerKind === 'relay_quiet' && !dismissedRelay) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 44,
          pointerEvents: 'none',
        }}
      >
        <div
          role="status"
          aria-live="polite"
          data-testid="ipc-transport-relay-note"
          style={{
            position: 'fixed',
            left: '50%',
            transform: 'translateX(-50%)',
            bottom: 24,
            zIndex: 44,
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            maxWidth: 'min(520px, calc(100vw - 32px))',
            padding: '8px 12px',
            borderRadius: 10,
            background: 'rgba(15, 23, 42, 0.82)',
            color: 'rgba(226, 232, 240, 0.88)',
            fontSize: 12,
            fontWeight: 450,
            border: '1px solid rgba(148, 163, 184, 0.22)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <span style={{ textAlign: 'center', flex: 1 }}>
            Using cloud fallback for transport — playback may be slower.
          </span>
          <button
            type="button"
            aria-label="Dismiss network transport note"
            data-testid="ipc-transport-relay-note-dismiss"
            onClick={onDismissRelay}
            style={{
              padding: '2px 6px',
              borderRadius: 6,
              border: 'none',
              background: 'transparent',
              color: 'rgba(226, 232, 240, 0.65)',
              fontSize: 16,
              lineHeight: 1,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
      </div>
    );
  }

  if (bannerKind !== 'broken_loud' || dismissedBroken) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 44,
        pointerEvents: 'none',
      }}
    >
      <div
        role="alert"
        aria-live="assertive"
        data-testid="ipc-transport-degraded-banner"
        style={{
          position: 'fixed',
          left: '50%',
          transform: 'translateX(-50%)',
          top: 76,
          zIndex: 44,
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 10,
          flexWrap: 'wrap',
          maxWidth: 'min(720px, calc(100vw - 32px))',
          padding: '10px 14px',
          borderRadius: 12,
          background: 'rgba(30, 41, 59, 0.94)',
          color: '#e2e8f0',
          fontSize: 13,
          fontWeight: 500,
          border: '1px solid rgba(248, 113, 113, 0.45)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 28px rgba(0,0,0,0.35)',
        }}
      >
        <span style={{ textAlign: 'center' }}>
          {brokenBannerMessage(updatePending)}
        </span>
        <button
          type="button"
          aria-label="Dismiss transport warning"
          data-testid="ipc-transport-degraded-banner-dismiss"
          onClick={onDismissBroken}
          style={{
            padding: '4px 8px',
            borderRadius: 6,
            border: 'none',
            background: 'transparent',
            color: 'rgba(226, 232, 240, 0.75)',
            fontSize: 18,
            lineHeight: 1,
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
